import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getExamResults, postEndExam } from '../api/tutor';

const buildErrorMessage = async (response, fallback) => {
  const status = response?.status;
  let detail = '';

  if (response) {
    try {
      const data = await response.json();
      detail = data?.detail || data?.message || '';
    } catch (error) {
      detail = '';
    }
  }

  const detailSuffix = detail ? ` - ${detail}` : '';
  return `${fallback} (status ${status ?? 'unknown'})${detailSuffix}`;
};

function ExamResultsPage() {
  const { sessionId } = useParams();
  const [results, setResults] = useState(null);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  const loadResults = useCallback(async () => {
    setStatus('loading_results');
    setErrorMessage('');
    setInfoMessage('');

    try {
      const response = await getExamResults(sessionId);

      if (response.status === 409) {
        setStatus('not_ready');
        setInfoMessage('Results not available until exam is ended and graded.');
        return;
      }

      if (!response.ok) {
        const message = await buildErrorMessage(response, 'Failed to load results.');
        throw new Error(message);
      }

      const data = await response.json();
      setResults(data);
      setErrorMessage('');
      setInfoMessage('');
      setStatus('ready');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message || 'Failed to load results.');
    }
  }, [sessionId]);

  const handleEndExam = async () => {
    setStatus('ending');
    setErrorMessage('');
    setInfoMessage('');

    try {
      const endResponse = await postEndExam(sessionId);

      if (!endResponse.ok && endResponse.status !== 409) {
        const message = await buildErrorMessage(endResponse, 'Failed to end exam.');
        throw new Error(message);
      }

      await loadResults();
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message || 'Failed to end exam.');
    }
  };

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  return (
    <div className="page page-shell page-stack">
      <section className="card exam-hero">
        <div className="page-header">
          <div>
          <p className="eyebrow">Exam mode</p>
          <h1>Exam Results</h1>
          <p>Review the finalized paper outcome inside the current implemented exam boundary.</p>
          </div>
          <div className="page-header-actions">
            <Link to="/dashboard" className="btn btn-secondary">Back to dashboard</Link>
          </div>
        </div>
      </section>
      {infoMessage ? <p className="notice notice-info">{infoMessage}</p> : null}
      {status === 'ending' ? <p>Ending exam and loading results...</p> : null}
      {status === 'loading_results' ? <p>Loading results...</p> : null}
      {errorMessage ? <p role="alert" className="notice notice-error">{errorMessage}</p> : null}
      {status === 'not_ready' ? (
        <section className="card exam-card">
          <p className="section-title">Finalize paper</p>
          <p className="muted-text">Results are only available after the exam has been explicitly ended.</p>
          <button type="button" className="btn btn-primary" onClick={handleEndExam} disabled={status === 'ending'}>
            End exam &amp; view results
          </button>
        </section>
      ) : null}
      {status === 'ready' && results ? (
        <section className="exam-results-grid">
          <div className="card exam-results-summary">
            <p className="section-title">Score</p>
            <p>
              Score: {results?.summary?.total_score ?? 'unknown'} /{' '}
              {results?.summary?.total_max ?? 'unknown'}
            </p>
            <p>Percent: {results?.summary?.percent ?? 'unknown'}</p>
            <p>Grade: {results?.summary?.grade ?? 'unknown'}</p>
          </div>
          <div className="card exam-results-summary">
            <p className="section-title">Paper</p>
            <p>Total questions: {results?.paper?.total_questions ?? 'unknown'}</p>
            <p>Total marks: {results?.paper?.total_marks ?? 'unknown'}</p>
            <p>Calculator policy: {results?.paper?.calculator_policy ?? 'unknown'}</p>
          </div>
          <ul className="data-list summary-card-wide">
            {(results?.questions || []).map((item) => (
              <li key={item.order_index} className="data-list-item">
                <div className="list-item-header" style={{ padding: 'var(--space-5)' }}>
                  <div>
                    <strong>Question {item.order_index}</strong>
                    <p className="muted-text">Status: {item.status}</p>
                  </div>
                  <span className="status-pill status-pill-marked">
                    {item.score ?? '0'}/{item.max_score ?? '0'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export default ExamResultsPage;
